import React, { useMemo } from "react";
import {
  AbsoluteFill,
  Audio,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { ThreeCanvas } from "@remotion/three";
import * as THREE from "three";

// ════════════════════════════════════════════════════════════════
// MASTER COMPOSITION: 100% THREE.JS 3D MOTION ENGINE (NO CHARACTER)
// Topik: Terciptanya AI (Mesin Kelereng Probabilitas / Next-Token Prediction)
// Visual: 100% Real 3D Physical Metaphor & Cinematic Lighting via Three.js
// ════════════════════════════════════════════════════════════════

export const AIKasinoKataMotion: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  const time = frame / fps;

  // Active scene determination based on audio narration
  // 0.0 - 7.5s (f0 - f225): Hook Komputer Tercerdas vs Mesin Tebak Kelereng 3D
  // 7.5 - 15.0s (f225 - f450): Aturan Manual IF-ELSE Kaku (3D Monolith Runtuh)
  // 15.0 - 18.0s (f450 - f540): Balik Cara Pikir (3D Matrix Sweep)
  // 18.0 - 22.5s (f540 - f675): Main Tebak Angka (3D Galton Board / Pin Pyramid)
  // 22.5 - 33.5s (f675 - f1005): Kelereng Kata Bouncing Lewat Paku Probabilitas 3D
  // 33.5 - 38.0s (f1005 - f1140): "Kucing makan..." -> Pilihan "IKAN" (88.4%) 3D Slot
  // 38.0 - 43.6s (f1140 - f1310): Outro Mesin Statistik 3D & Koin Emas Melayang
  let currentScene = 1;
  if (time >= 38.0) currentScene = 6;
  else if (time >= 33.5) currentScene = 5;
  else if (time >= 22.5) currentScene = 4;
  else if (time >= 15.0) currentScene = 3;
  else if (time >= 7.5) currentScene = 2;
  else currentScene = 1;

  // Kinetic subtitle text
  let currentSubtitle = "";
  let highlightedWord = "";

  if (time < 3.8) {
    currentSubtitle = "Sadar gak sih, komputer tercerdas di dunia hari ini...";
    highlightedWord = "KOMPUTER TERCERDAS";
  } else if (time < 7.5) {
    currentSubtitle = "Cara kerjanya mirip banget sama mesin tebak kelereng di pasar malam!";
    highlightedWord = "MESIN KELERENG 3D";
  } else if (time < 11.2) {
    currentSubtitle = "Dulu ilmuwan pusing bikin AI nulis jutaan aturan logika manual...";
    highlightedWord = "ATURAN MANUAL";
  } else if (time < 15.0) {
    currentSubtitle = "Dan hasilnya? Tetap kaku dan gampang error!";
    highlightedWord = "TETAP KAKU";
  } else if (time < 18.0) {
    currentSubtitle = "Sampai akhirnya, mereka balik cara pikirnya 180 derajat!";
    highlightedWord = "BALIK CARA PIKIR";
  } else if (time < 22.5) {
    currentSubtitle = "Daripada diajarin mikir, mending mesinnya disuruh main tebak angka!";
    highlightedWord = "MAIN TEBAK ANGKA";
  } else if (time < 28.0) {
    currentSubtitle = "Tiap lo ketik satu kata, dilempar kayak kelereng ngelewatin paku probabilitas...";
    highlightedWord = "PAKU PROBABILITAS";
  } else if (time < 33.5) {
    currentSubtitle = "Mesin gak punya kesadaran, cuma ngitung kata apa yang paling mungkin berikutnya!";
    highlightedWord = "NEXT-TOKEN ENGINE";
  } else if (time < 38.0) {
    currentSubtitle = "Ketik 'Kucing makan...', kelereng otomatis meluncur jatuh ke kata 'IKAN'!";
    highlightedWord = "88.4% IKAN";
  } else {
    currentSubtitle = "AI bukan otak ajaib, tapi mesin tebak kata statistik yang kelewat jago!";
    highlightedWord = "PROBABILITAS STATISTIK";
  }

  // Dynamic Camera Orbit based on scene
  const camX = Math.sin(frame * 0.02) * 0.35;
  const camY =
    currentScene === 5
      ? -0.4
      : currentScene === 4
      ? 0.1
      : currentScene === 2
      ? -0.1
      : 0.1;
  const camZ =
    currentScene === 5
      ? 4.4
      : currentScene === 4
      ? 5.0
      : currentScene === 1
      ? 5.2
      : 4.8;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#050608",
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
        color: "#FFFFFF",
        overflow: "hidden",
      }}
    >
      {/* 0. AUDIO VO MASTERED VIA 9ROUTER */}
      <Audio src={staticFile("vo_kasino_kata_ai.mp3")} />

      {/* 1. REAL 3D THREE.JS WEBGL CANVAS (FULL SCREEN) */}
      <div style={{ position: "absolute", inset: 0, zIndex: 10 }}>
        <ThreeCanvas
          width={width}
          height={height}
          camera={{
            position: [camX, camY, camZ],
            fov: 46,
          }}
        >
          {/* Studio Lights (Optimized for Fast WebGL Render) */}
          <ambientLight intensity={2.2} color="#CBD5E1" />
          <directionalLight
            position={[5, 12, 8]}
            intensity={4.5}
            color="#FFFFFF"
          />
          {/* Cyber Cyan Rim Light */}
          <directionalLight position={[-8, 6, -4]} intensity={5.5} color="#00F0FF" />
          {/* Amber Warm Glow */}
          <directionalLight position={[6, -4, 3]} intensity={3.5} color="#F59E0B" />
          {/* Neon Point Light */}
          <pointLight position={[0, 1.5, 2.5]} intensity={2.8} color="#38BDF8" distance={8} />

          {/* 3D SCENE OBJECTS */}
          <PachinkoMachine3D frame={frame} fps={fps} scene={currentScene} />
        </ThreeCanvas>
      </div>

      {/* 2. TOP BRANDING BAR (TikTok Safe Zone Y: 220px - 280px) */}
      <div
        style={{
          position: "absolute",
          top: 240,
          left: 60,
          right: 60,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 50,
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            background: "rgba(10, 12, 18, 0.88)",
            backdropFilter: "blur(14px)",
            padding: "12px 22px",
            borderRadius: 30,
            border: "1px solid rgba(0, 240, 255, 0.3)",
            boxShadow: "0 8px 30px rgba(0, 0, 0, 0.6)",
          }}
        >
          <div
            style={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              backgroundColor: "#00F0FF",
              boxShadow: "0 0 12px #00F0FF",
            }}
          />
          <span style={{ fontSize: 24, fontWeight: 900, letterSpacing: 1.5, color: "#FFFFFF" }}>
            ZURA • AI LAB
          </span>
          <span
            style={{
              fontSize: 16,
              fontWeight: 800,
              background: "#FF6B00",
              color: "#000",
              padding: "2px 8px",
              borderRadius: 6,
            }}
          >
            3D THREE.JS
          </span>
        </div>

        <div
          style={{
            background: "rgba(245, 158, 11, 0.18)",
            border: "1px solid rgba(245, 158, 11, 0.5)",
            color: "#FBBF24",
            padding: "10px 22px",
            borderRadius: 24,
            fontSize: 20,
            fontWeight: 800,
            letterSpacing: 1.2,
            backdropFilter: "blur(10px)",
          }}
        >
          NEXT-TOKEN ENGINE
        </div>
      </div>

      {/* 3. DYNAMIC ON-SCREEN HEADERS (Safe Zone Y: 320px - 440px) */}
      <div
        style={{
          position: "absolute",
          top: 330,
          left: 60,
          right: 60,
          textAlign: "center",
          zIndex: 40,
          pointerEvents: "none",
        }}
      >
        {currentScene === 1 && (
          <div
            style={{
              fontSize: 66,
              fontWeight: 900,
              lineHeight: 1.1,
              textTransform: "uppercase",
              textShadow: "0 4px 20px rgba(0,0,0,0.8)",
            }}
          >
            KOMPUTER <span style={{ color: "#00F0FF" }}>TERCERDAS</span>
            <br />
            CUMA <span style={{ color: "#F59E0B" }}>MESIN TEBAK?</span>
          </div>
        )}

        {currentScene === 2 && (
          <div
            style={{
              fontSize: 64,
              fontWeight: 900,
              lineHeight: 1.1,
              textTransform: "uppercase",
              textShadow: "0 4px 20px rgba(0,0,0,0.8)",
            }}
          >
            LOGIKA LAMA: <span style={{ color: "#EF4444" }}>KUTUKAN IF-ELSE</span>
          </div>
        )}

        {currentScene === 3 && (
          <div
            style={{
              fontSize: 64,
              fontWeight: 900,
              lineHeight: 1.1,
              textTransform: "uppercase",
              textShadow: "0 4px 20px rgba(0,0,0,0.8)",
            }}
          >
            BALIK CARA PIKIR: <span style={{ color: "#10B981" }}>TEBAK ANGKA</span>
          </div>
        )}

        {currentScene === 4 && (
          <div
            style={{
              fontSize: 62,
              fontWeight: 900,
              lineHeight: 1.1,
              textTransform: "uppercase",
              textShadow: "0 4px 20px rgba(0,0,0,0.8)",
            }}
          >
            KELERENG <span style={{ color: "#00F0FF" }}>PROBABILITAS</span> 3D
          </div>
        )}

        {currentScene === 5 && (
          <div
            style={{
              fontSize: 62,
              fontWeight: 900,
              lineHeight: 1.1,
              textTransform: "uppercase",
              textShadow: "0 4px 20px rgba(0,0,0,0.8)",
            }}
          >
            PROMPT: <span style={{ color: "#10B981" }}>"KUCING MAKAN..."</span>
          </div>
        )}

        {currentScene === 6 && (
          <div
            style={{
              fontSize: 64,
              fontWeight: 900,
              lineHeight: 1.1,
              textTransform: "uppercase",
              textShadow: "0 4px 20px rgba(0,0,0,0.8)",
            }}
          >
            BUKAN OTAK AJAIB...
            <br />
            <span
              style={{
                background: "linear-gradient(90deg, #F59E0B, #FF6B00)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              MESIN STATISTIK KATA!
            </span>
          </div>
        )}
      </div>

      {/* 4. KINETIC SUBTITLE LOWER-THIRD (Safe Zone Y: 1280px - 1460px) */}
      <div
        style={{
          position: "absolute",
          bottom: 460,
          left: 60,
          right: 60,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          zIndex: 60,
          pointerEvents: "none",
        }}
      >
        {highlightedWord && (
          <div
            style={{
              background: "linear-gradient(90deg, #FF6B00, #F59E0B)",
              color: "#000000",
              fontWeight: 900,
              fontSize: 26,
              padding: "6px 22px",
              borderRadius: 8,
              letterSpacing: 2,
              marginBottom: 14,
              boxShadow: "0 6px 20px rgba(255, 107, 0, 0.4)",
              textTransform: "uppercase",
            }}
          >
            {highlightedWord}
          </div>
        )}
        <div
          style={{
            fontSize: 36,
            fontWeight: 800,
            lineHeight: 1.35,
            color: "#FFFFFF",
            textShadow: "0 4px 16px rgba(0,0,0,0.95)",
            background: "rgba(10, 12, 18, 0.82)",
            backdropFilter: "blur(12px)",
            padding: "16px 28px",
            borderRadius: 16,
            border: "1px solid rgba(255, 255, 255, 0.16)",
          }}
        >
          {currentSubtitle}
        </div>
      </div>

      {/* 5. CINEMATIC EDGES */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          boxShadow: "inset 0 0 140px rgba(0, 0, 0, 0.8)",
          pointerEvents: "none",
        }}
      />
    </AbsoluteFill>
  );
};

// ════════════════════════════════════════════════════════════════
// 3D PACHINKO & MACHINE COMPONENTS IN THREE.JS
// ════════════════════════════════════════════════════════════════

const PachinkoMachine3D: React.FC<{
  frame: number;
  fps: number;
  scene: number;
}> = ({ frame, fps, scene }) => {
  // Machine rotation & floating
  const floatY = Math.sin(frame * 0.04) * 0.08;
  const tiltX = -0.15 + Math.sin(frame * 0.03) * 0.04;
  const rotY = Math.sin(frame * 0.02) * 0.12;

  // Peg grid positions (Plinko triangle)
  const pegs = useMemo(() => {
    const list: [number, number, number][] = [];
    const rows = 8;
    for (let r = 0; r < rows; r++) {
      const count = r + 3;
      const startX = -(count - 1) * 0.22;
      for (let c = 0; c < count; c++) {
        list.push([startX + c * 0.44, 1.4 - r * 0.38, 0.08]);
      }
    }
    return list;
  }, []);

  // Material presets
  const goldMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: "#F59E0B",
        metalness: 0.9,
        roughness: 0.15,
      }),
    []
  );

  const cyanGlowMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: "#00F0FF",
        emissive: new THREE.Color("#00F0FF"),
        emissiveIntensity: 0.8,
        metalness: 0.8,
        roughness: 0.2,
      }),
    []
  );

  const glassMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: "#1E293B",
        metalness: 0.8,
        roughness: 0.2,
        transparent: true,
        opacity: 0.45,
      }),
    []
  );

  const frameMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: "#0B0E14",
        metalness: 0.6,
        roughness: 0.4,
      }),
    []
  );

  return (
    <group position={[0, floatY, 0]} rotation={[tiltX, rotY, 0]}>
      {/* ─── 1. PACHINKO BACKBOARD & CABINET ─── */}
      <mesh material={frameMaterial} position={[0, 0, -0.1]}>
        <boxGeometry args={[3.2, 4.4, 0.3]} />
      </mesh>

      {/* Glowing Neon Border Frame */}
      <mesh position={[0, 0, 0.06]}>
        <boxGeometry args={[3.04, 4.24, 0.04]} />
        <meshBasicMaterial color={scene === 2 ? "#EF4444" : "#00F0FF"} wireframe />
      </mesh>

      {/* Glass Front Cover */}
      <mesh material={glassMaterial} position={[0, 0, 0.2]}>
        <boxGeometry args={[3.0, 4.2, 0.05]} />
      </mesh>

      {/* ─── 2. TOP FUNNEL (TOKEN DISPENSER) ─── */}
      <group position={[0, 1.85, 0.1]}>
        <mesh material={goldMaterial}>
          <cylinderGeometry args={[0.4, 0.2, 0.4, 24]} />
        </mesh>
        <mesh position={[0, 0.22, 0]}>
          <ringGeometry args={[0.35, 0.45, 24]} />
          <meshBasicMaterial color="#00F0FF" />
        </mesh>
      </group>

      {/* ─── 3. TRIANGULAR PEGS MATRIX (Paku-Paku Emas) ─── */}
      {scene !== 2 &&
        pegs.map((pos, i) => (
          <mesh
            key={`peg-${i}`}
            material={goldMaterial}
            position={pos}
            rotation={[Math.PI / 2, 0, 0]}
          >
            <cylinderGeometry args={[0.035, 0.035, 0.24, 16]} />
          </mesh>
        ))}

      {/* ─── 4. SCENE 2 SPECIAL: SHATTERED IF-ELSE MONOLITHS ─── */}
      {scene === 2 && (
        <group position={[0, 0, 0.2]}>
          {[-1.0, -0.5, 0, 0.5, 1.0].map((x, idx) => {
            const shatterY = Math.sin(frame * 0.1 + idx) * 0.2;
            const rotZ = Math.sin(frame * 0.08 + idx) * 0.3;
            return (
              <mesh
                key={`if-block-${idx}`}
                position={[x, -0.2 + shatterY, 0]}
                rotation={[0.1, 0.2, rotZ]}
              >
                <boxGeometry args={[0.38, 0.9, 0.2]} />
                <meshStandardMaterial
                  color="#EF4444"
                  emissive="#7F1D1D"
                  roughness={0.3}
                  metalness={0.4}
                />
              </mesh>
            );
          })}
        </group>
      )}

      {/* ─── 5. DYNAMIC BOUNCING MARBLES (Kelereng Neon Bertumbukan) ─── */}
      {scene !== 2 && (
        <group>
          {/* Main Hero Marble (Cyan Winner) */}
          <mesh
            material={cyanGlowMaterial}
            position={[
              Math.sin(frame * 0.22) * 0.5,
              1.6 - ((frame * 0.08) % 3.2),
              0.12,
            ]}
          >
            <sphereGeometry args={[0.11, 24, 24]} />
          </mesh>

          {/* Secondary Orange Marble */}
          <mesh
            position={[
              Math.cos((frame + 30) * 0.18) * 0.7,
              1.6 - (((frame + 45) * 0.075) % 3.2),
              0.12,
            ]}
          >
            <sphereGeometry args={[0.09, 24, 24]} />
            <meshStandardMaterial
              color="#FF6B00"
              emissive="#FF6B00"
              emissiveIntensity={0.6}
              metalness={0.8}
            />
          </mesh>

          {/* Purple Marble */}
          <mesh
            position={[
              Math.sin((frame + 60) * 0.16) * 0.6,
              1.6 - (((frame + 90) * 0.07) % 3.2),
              0.12,
            ]}
          >
            <sphereGeometry args={[0.095, 24, 24]} />
            <meshStandardMaterial
              color="#A855F7"
              emissive="#A855F7"
              emissiveIntensity={0.6}
              metalness={0.8}
            />
          </mesh>
        </group>
      )}

      {/* ─── 6. BOTTOM BINS (SLOTS KATA: IKAN 88%, RUMPUT 7%, BATU 0.1%) ─── */}
      <group position={[0, -1.6, 0.12]}>
        {/* Divider Plates */}
        {[-1.2, -0.6, 0, 0.6, 1.2].map((bx, i) => (
          <mesh key={`bin-div-${i}`} material={goldMaterial} position={[bx, 0, 0]}>
            <boxGeometry args={[0.04, 0.7, 0.22]} />
          </mesh>
        ))}

        {/* Slot Backing Lights */}
        {/* Slot 1: dan / lain */}
        <mesh position={[-0.9, 0, -0.02]}>
          <boxGeometry args={[0.55, 0.65, 0.05]} />
          <meshStandardMaterial color="#1E293B" roughness={0.7} />
        </mesh>

        {/* Slot 2: rumput (7%) */}
        <mesh position={[-0.3, 0, -0.02]}>
          <boxGeometry args={[0.55, 0.65, 0.05]} />
          <meshStandardMaterial color="#334155" roughness={0.6} />
        </mesh>

        {/* Slot 3: WINNER "IKAN" (88.4%) - Radiant Emerald Glow */}
        <mesh position={[0.3, 0, -0.02]}>
          <boxGeometry args={[0.55, 0.65, 0.05]} />
          <meshStandardMaterial
            color="#10B981"
            emissive="#10B981"
            emissiveIntensity={scene === 5 ? 1.5 : 0.8}
            roughness={0.2}
          />
        </mesh>

        {/* Slot 4: batu (0.1%) */}
        <mesh position={[0.9, 0, -0.02]}>
          <boxGeometry args={[0.55, 0.65, 0.05]} />
          <meshStandardMaterial color="#1E293B" roughness={0.7} />
        </mesh>
      </group>

      {/* ─── 7. SCENE 6 OUTRO: 3D SPINNING GOLD COINS ─── */}
      {scene === 6 && (
        <group position={[0, 0, 0.8]}>
          {[-1.2, -0.4, 0.4, 1.2].map((cx, cIdx) => {
            const coinRotY = frame * 0.06 + cIdx * 1.2;
            const coinFloatY = Math.sin(frame * 0.1 + cIdx) * 0.15;
            return (
              <mesh
                key={`coin-${cIdx}`}
                position={[cx, 0.2 + coinFloatY, 0]}
                rotation={[0.3, coinRotY, 0.1]}
              >
                <cylinderGeometry args={[0.32, 0.32, 0.05, 32]} />
                <meshStandardMaterial
                  color="#FBBF24"
                  metalness={0.95}
                  roughness={0.1}
                  emissive="#D97706"
                  emissiveIntensity={0.3}
                />
              </mesh>
            );
          })}
        </group>
      )}
    </group>
  );
};
